import { CirclePlus, Loader2, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import Modal from '@/components/Common/Modal';
import { Button } from '@/components/Common/button';
import notify from '@/lib/notify';
import {
  InterviewTemplate,
  InterviewTemplatePayload,
  Question as BackendQuestion,
  TemplateQuestionInForm,
} from '@/types';
import {
  createInterviewTemplate,
  fetchInterviewTemplateById,
  updateInterviewTemplate,
} from '@/services/interviewTemplateService';

interface InterviewTemplateFormModalProps {
  open: boolean;
  /** When set, the modal edits the given template; otherwise it creates a new one. */
  templateId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  name: string;
  description: string;
  questions: TemplateQuestionInForm[];
}

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  questions: [{ id: uuidv4(), text: '', order: 1 }],
});

/**
 * Modal form to create or edit an interview (guided) template.
 * Ports the previous full-page TemplateForm into a dialog so both template
 * families are managed inline from the organization Templates tab.
 */
export const InterviewTemplateFormModal: React.FC<InterviewTemplateFormModalProps> = ({
  open,
  templateId,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const isEditing = Boolean(templateId);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEditing && templateId) {
      setLoading(true);
      fetchInterviewTemplateById(templateId)
        .then((data: InterviewTemplate) => {
          setForm({
            name: data.name,
            description: data.description || '',
            questions: data.questions.map(
              (q: BackendQuestion): TemplateQuestionInForm => ({
                id: uuidv4(),
                text: q.text,
                order: q.order,
              }),
            ),
          });
        })
        .catch((err) => {
          console.error('Failed to load interview template:', err);
          notify.error(t('templates.loadError', 'Impossible de charger le modèle'));
          onClose();
        })
        .finally(() => setLoading(false));
    } else {
      setForm(emptyForm());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateId, isEditing]);

  const handleQuestionChange = (
    id: string,
    field: 'text' | 'order',
    value: string | number,
  ) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q) =>
        q.id === id
          ? { ...q, [field]: field === 'order' ? Number(value) || 1 : value }
          : q,
      ),
    }));
  };

  const handleAddQuestion = () => {
    setForm((prev) => {
      const nextOrder =
        prev.questions.length > 0
          ? Math.max(...prev.questions.map((q) => q.order)) + 1
          : 1;
      return {
        ...prev,
        questions: [...prev.questions, { id: uuidv4(), text: '', order: nextOrder }],
      };
    });
  };

  const handleRemoveQuestion = (id: string) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.filter((q) => q.id !== id),
    }));
  };

  const isInvalid = useCallback(
    () =>
      !form.name.trim() ||
      form.questions.length === 0 ||
      form.questions.some((q) => !q.text.trim() || q.order <= 0),
    [form.name, form.questions],
  );

  const handleSubmit = async () => {
    if (isInvalid()) {
      notify.error(t('templates.nameRequired', 'Le nom et au moins une question sont requis'));
      return;
    }
    setSaving(true);
    const payload: InterviewTemplatePayload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      template_type: 'cadrage',
      questions: form.questions.map((q) => ({ text: q.text.trim(), order: q.order })),
    };
    try {
      if (isEditing && templateId) {
        await updateInterviewTemplate(templateId, payload);
        notify.success(t('templates.updated', 'Modèle mis à jour'));
      } else {
        await createInterviewTemplate(payload);
        notify.success(t('templates.created', 'Modèle créé'));
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error('Failed to save interview template:', err);
      notify.error(
        err?.response?.data?.detail ||
          t('templates.updateError', "Erreur lors de l'enregistrement du modèle"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={
        isEditing
          ? t('templates.editTemplate', 'Modifier le modèle')
          : t('templates.newTemplate', "Nouveau modèle d'entretien")
      }
      onClose={() => {
        if (!saving) onClose();
      }}
      className="max-w-2xl"
      bodyClassName="max-h-[70vh] overflow-y-auto"
      hideDefaultFooter
    >
      {loading ? (
        <p className="text-sm text-muted-foreground flex items-center gap-2 py-6">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('templates.loading', 'Chargement...')}
        </p>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium">
              {t('templates.nameLabel', 'Nom')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
              maxLength={255}
              required
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium">
              {t('templates.descriptionOptional', 'Description (optionnelle)')}
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
              rows={2}
            />
          </div>

          <div className="space-y-3 border-t border-border pt-4">
            <h3 className="text-sm font-medium">
              {t('templates.guidedQuestions', 'Questions guidées')}
            </h3>
            {form.questions.map((q) => (
              <div key={q.id} className="p-3 border border-border rounded-md bg-muted/40">
                <div className="flex items-end gap-2">
                  <div className="flex-grow space-y-1">
                    <label className="block text-xs font-medium text-muted-foreground">
                      {t('templates.questionText', 'Question')}
                    </label>
                    <textarea
                      rows={2}
                      value={q.text}
                      onChange={(e) => handleQuestionChange(q.id, 'text', e.target.value)}
                      className="w-full px-2 py-1.5 border border-border rounded-md bg-background text-sm"
                      required
                    />
                  </div>
                  <div className="w-20 space-y-1">
                    <label className="block text-xs font-medium text-muted-foreground">
                      {t('templates.order', 'Ordre')}
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={q.order}
                      onChange={(e) =>
                        handleQuestionChange(q.id, 'order', parseInt(e.target.value, 10))
                      }
                      className="w-full px-2 py-1.5 border border-border rounded-md bg-background text-sm"
                      required
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={() => handleRemoveQuestion(q.id)}
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    aria-label={t('templates.removeQuestionAria', 'Supprimer la question')}
                    disabled={form.questions.length <= 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              onClick={handleAddQuestion}
              variant="outline"
              className="border-dashed"
            >
              <CirclePlus className="h-4 w-4 mr-2" />
              {t('templates.addQuestion', 'Ajouter une question')}
            </Button>
          </div>

          <div className="flex gap-2 justify-end pt-2 border-t border-border">
            <Button onClick={onClose} variant="outline" disabled={saving}>
              {t('templates.cancel', 'Annuler')}
            </Button>
            <Button onClick={() => void handleSubmit()} disabled={saving || isInvalid()}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing
                ? t('templates.update', 'Enregistrer')
                : t('templates.create', 'Créer')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default InterviewTemplateFormModal;
